import { inject, Injectable, Injector, type ComponentRef } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  BaseRouteReuseStrategy,
  DetachedRouteHandle,
  NavigationEnd,
  Route,
  Router,
} from '@angular/router';
import {
  GridReturnStateService,
  returnsFromEditor,
} from '@sensika/ag-grid-kit';
import { filter } from 'rxjs/operators';

/** Route data flag of a grid route kept alive while the editor its opener remembered is open. */
export const GRID_ROUTE_REUSE_FLAG = 'reuseAcrossEditor';

/**
 * Keeps a `gridReturnState` grid's component instance across its route editor
 * round trip (WAP-2025 slice 2): the grid route is detached when the navigation
 * committed targets the editor its opener remembered, re-attached when the grid
 * is entered back from under that editor, and dropped (destroyed) on any other
 * navigation - another module, a company switch, a logout. One handle at a time;
 * every route without the flag keeps Angular's default lifetime.
 */
@Injectable({ providedIn: 'root' })
export class GridRouteReuseStrategy extends BaseRouteReuseStrategy {
  /** The Router constructs this strategy, so both are resolved on first use, never in the constructor (NG0200). */
  private readonly injector = inject(Injector);
  private watching = false;

  private stored: {
    config: Route;
    handle: DetachedRouteHandle;
    editorUrl: string;
  } | null = null;

  private get router(): Router {
    return this.injector.get(Router);
  }

  private get returnState(): GridReturnStateService {
    return this.injector.get(GridReturnStateService);
  }

  /** Any completed navigation that is not under the editor drops the handle. */
  private watchNavigations(): void {
    if (this.watching) {
      return;
    }
    this.watching = true;
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (
          this.stored &&
          !returnsFromEditor(event.urlAfterRedirects, this.stored.editorUrl)
        ) {
          this.drop();
        }
      });
  }

  override shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (!route.routeConfig?.data?.[GRID_ROUTE_REUSE_FLAG]) {
      return false;
    }
    const editorUrl = this.returnState.rememberedEditorUrl;
    const target = this.router.getCurrentNavigation()?.finalUrl;
    return (
      !!editorUrl &&
      !!target &&
      path(this.router.serializeUrl(target)) === path(editorUrl)
    );
  }

  /** Angular stores the handle after `shouldDetach`, and stores null after a re-attach. */
  override store(
    route: ActivatedRouteSnapshot,
    handle: DetachedRouteHandle | null,
  ): void {
    if (!handle) {
      if (this.stored && route.routeConfig === this.stored.config) {
        this.stored = null;
        this.returnState.reattached$.next(route.routeConfig);
      }
      return;
    }
    this.drop();
    this.watchNavigations();
    this.stored = {
      config: route.routeConfig as Route,
      handle,
      editorUrl: this.returnState.rememberedEditorUrl as string,
    };
  }

  override shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!this.stored || route.routeConfig !== this.stored.config) {
      return false;
    }
    const previous =
      this.router.getCurrentNavigation()?.previousNavigation?.finalUrl;
    if (
      previous &&
      returnsFromEditor(
        this.router.serializeUrl(previous),
        this.stored.editorUrl,
      )
    ) {
      return true;
    }
    this.drop();
    return false;
  }

  override retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this.stored && route.routeConfig === this.stored.config
      ? this.stored.handle
      : null;
  }

  /** A handle nobody re-attaches holds a live component: destroy it. */
  private drop(): void {
    const handle = this.stored?.handle as
      | { componentRef?: ComponentRef<unknown> }
      | undefined;
    this.stored = null;
    handle?.componentRef?.destroy();
  }
}

function path(url: string): string {
  return url.split('?')[0];
}
